import React from "react";
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Box,
  InlineGrid,
  Badge,
} from "@shopify/polaris";
import { TooltipDemoOnly as Tooltip } from "./TooltipDemoOnly";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const PULSE_STYLE = `
  @keyframes roi-pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(48, 164, 108, 0.7);
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 8px rgba(48, 164, 108, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(48, 164, 108, 0);
    }
  }
  .roi-icon-pulse {
    animation: roi-pulse 2s infinite cubic-bezier(0.66, 0, 0, 1);
  }
`;

interface AnalyticsOverviewProps {
  revenueData: { date: string; revenue: number }[];
  intentData: { name: string; value: number }[];
  totalSavedRevenue: number;
  monthlyRevenueSaved: number;
  totalReturns: number;
  automationRate: number;
  annualizedSavings: number;
  retentionLift: number;
  roiMultiple: number;
  avgSavedOrder: number;
  shopLocale?: string;
  shopCurrency?: string;
}

const formatCurrency = (value: number, locale = "en-US", currency = "USD") => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatCurrencyDetailed = (value: number, locale = "en-US", currency = "USD") => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
  }).format(value);
};

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#FF6B6B", "#4DABF7", "#FF922B"];

export function AnalyticsOverview({
  revenueData,
  intentData,
  totalSavedRevenue,
  monthlyRevenueSaved,
  totalReturns,
  automationRate,
  annualizedSavings,
  retentionLift,
  roiMultiple,
  avgSavedOrder,
  shopLocale = "en-US",
  shopCurrency = "USD",
}: AnalyticsOverviewProps) {
  // totalReturns is provided by props

  return (
    <BlockStack gap="400">
      <style dangerouslySetInnerHTML={{ __html: PULSE_STYLE }} />
      <InlineGrid columns={["twoThirds", "oneThird"]} gap="400">
        <Box borderRadius="300">
          <Card>
            <BlockStack gap="200">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="050">
                  <InlineStack gap="100" blockAlign="center">
                    <Text as="h2" variant="headingMd">
                      LTV Protected (Revenue Saved)
                    </Text>
                    <Tooltip persist content="Estimated revenue recovered from automated subscription retention actions.">
                      <span
                        className="roi-icon-pulse"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          backgroundColor: '#30a46c',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: 'white',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease-in-out',
                          lineHeight: '1',
                          paddingBottom: '1px'
                        }}
                      >
                        i
                      </span>
                    </Tooltip>
                  </InlineStack>
                  <Text variant="headingXl" as="p" fontWeight="bold">
                    <span data-testid="analytics-total-saved-revenue">{formatCurrencyDetailed(totalSavedRevenue, shopLocale, shopCurrency)}</span>
                  </Text>
                  <Text variant="bodyXs" tone="subdued" as="p">
                    Estimated annualized savings: {formatCurrency(annualizedSavings, shopLocale, shopCurrency)}
                  </Text>
                </BlockStack>
                <Badge tone="success">{`+${formatCurrency(monthlyRevenueSaved, shopLocale, shopCurrency)}`}</Badge>
              </InlineStack>
              <div style={{ height: '250px', width: '100%', position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#30a46c" stopOpacity={0.1} />
                        <stop offset="95%" stopColor="#30a46c" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f1f1" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => formatCurrency(value, shopLocale, shopCurrency)}
                    />
                    <RechartsTooltip
                      formatter={(value: any) => [formatCurrency(Number(value || 0), shopLocale, shopCurrency), "Revenue"]}
                      contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#30a46c"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorRevenue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </BlockStack>
          </Card>
        </Box>

        <Box borderRadius="300">
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">
                Intent Distribution
              </Text>
              <div style={{ height: '250px', width: '100%', position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <PieChart>
                    <Pie
                      data={intentData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {intentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value: any) => {
                        const val = Number(value || 0);
                        const percent = totalReturns > 0 ? ((val / totalReturns) * 100).toFixed(1) : 0;
                        return [`${val} Returns (${percent}%)`, "Count"];
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconSize={10}
                      formatter={(value, entry: any) => `${value} (${entry.payload.value})`}
                      wrapperStyle={{
                        paddingTop: '10px',
                        fontSize: '11px',
                        width: '100%',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </BlockStack>
          </Card>
        </Box>
      </InlineGrid>

      <InlineGrid columns={4} gap="400">
        <Box borderRadius="300">
          <Card>
            <Tooltip content="Percentage of return events where AI successfully intervened to save the subscription.">
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued" as="p">
                  Automation Rate
                </Text>
                <Text variant="headingLg" as="p">
                  <span data-testid="analytics-auto-rate">{Math.round(automationRate * 100)}%</span>
                </Text>
              </BlockStack>
            </Tooltip>
          </Card>
        </Box>
        <Box borderRadius="300">
          <Card>
            <Tooltip content="The multiple of revenue saved vs. your Revivify AI subscription cost.">
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued" as="p">
                  Platform ROI
                </Text>
                <InlineStack gap="200" blockAlign="center">
                  <Text variant="headingLg" as="p" tone="success">
                    <span data-testid="analytics-platform-roi">{roiMultiple.toFixed(1)}x</span>
                  </Text>
                  <Badge tone="success">High Value</Badge>
                </InlineStack>
              </BlockStack>
            </Tooltip>
          </Card>
        </Box>
        <Box borderRadius="300">
          <Card>
            <Tooltip content="The average dollar value of a subscription successfully recovered by Revivify AI.">
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued" as="p">
                  Avg. Saved Order
                </Text>
                <Text variant="headingLg" as="p">
                  <span data-testid="analytics-avg-saved-order">{formatCurrencyDetailed(avgSavedOrder, shopLocale, shopCurrency)}</span>
                </Text>
              </BlockStack>
            </Tooltip>
          </Card>
        </Box>
        <Box borderRadius="300">
          <Card>
            <Tooltip content="Your total saved revenue projected over 12 months based on current performance.">
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued" as="p">
                  Est. Annualized Savings
                </Text>
                <Text variant="headingLg" as="p" tone="success">
                  <span data-testid="analytics-annualized-savings">{formatCurrency(annualizedSavings, shopLocale, shopCurrency)}</span>
                </Text>
              </BlockStack>
            </Tooltip>
          </Card>
        </Box>
      </InlineGrid>
    </BlockStack>
  );
}
